import React, { FC, useRef, useState } from 'react';
import { Box, Button, Modal, Typography } from '@material-ui/core';
import ReactCrop from 'react-image-crop';
import clsx from 'clsx';
import { Crop } from 'react-image-crop/dist/types';
import { useStyles } from '../../styles';
import 'react-image-crop/dist/ReactCrop.css';

interface Props {
    image: string;
    isOpen: boolean;
    onClose: () => void;
    handleImageChange: (image: string) => void;
    changeFile: () => void;
}

const ImageCropper: FC<Props> = ({ image, isOpen, onClose, handleImageChange, changeFile }) => {
    const classes = useStyles();

    const imageRef = useRef<HTMLImageElement | null>(null);

    const [crop, setCrop] = useState<Crop>({ x: 0, y: 0, unit: '%', width: 100, height: 100 });
    const [croppedImage, setCroppedImage] = useState<string>();

    const getCroppedImg = async (image: any, crop: Crop): Promise<Blob | null> => {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height,
        );

        return new Promise(resolve => {
            canvas.toBlob(
                (blob: Blob | null) => {
                    resolve(blob);
                },
                'image/jpeg',
                1,
            );
        });
    };

    const onCropComplete = async (crop: Crop) => {
        if (imageRef && crop.width && crop.height) {
            const croppedImageUrl: Blob | null = await getCroppedImg(imageRef.current, crop);

            if (croppedImageUrl) {
                setCroppedImage(URL.createObjectURL(croppedImageUrl));
            }
        }
    };

    const saveCroppedImage = () => {
        if (croppedImage) handleImageChange(croppedImage);
    };

    const onCropChange = (crop: Crop) => {
        setCrop(crop);
    };

    return (
        <Modal open={isOpen} onClose={onClose} disableEnforceFocus>
            <Box className={classes.modal}>
                <Box className={classes.modalHeader}>
                    <Typography className={classes.modalTitle}>Edit your Event Photo</Typography>
                </Box>

                <Box className={classes.cropperContainer}>
                    {!!image && (
                        <ReactCrop
                            ruleOfThirds
                            crop={crop}
                            onChange={onCropChange}
                            onComplete={onCropComplete}
                        >
                            <img ref={imageRef} src={image} alt="crop" />
                        </ReactCrop>
                    )}
                </Box>

                <Box className={classes.modalFooter}>
                    <Button
                        onClick={changeFile}
                        className={clsx(classes.cropperButton, classes.selectFileButton)}
                    >
                        Select a file
                    </Button>
                    <Button
                        className={clsx(classes.cropperButton, classes.saveButton)}
                        onClick={saveCroppedImage}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ImageCropper;
